<div #canvasOuterContainer class="canvas-outer-container" (mousedown)="clearSelectedWidget()">
  @let designDimensions = designDimensions$ | async;
  @let arParts = aspectRatioParts$ | async;

  <ng-template #canvasContent>
    <div
      class="projector-canvas"
      [ngClass]="draggable ? 'edit' : 'display'"
      [class.responsive]="responsive"
      [style.width.px]="responsive ? null : designDimensions?.width"
      [style.height.px]="responsive ? null : designDimensions?.height"
      [style.--ar-w]="responsive ? arParts?.w : null"
      [style.--ar-h]="responsive ? arParts?.h : null"
      [style.background-color]="getResolvedColor(backgroundColor)"
      [style.background-image]="backgroundImage ? 'url(' + backgroundImage + ')' : null"
      [style.background-size]="backgroundImage ? (backgroundImageCover ? 'cover' : 'contain') : null"
      [style.background-position]="backgroundImage ? 'center' : null"
      [style.background-repeat]="backgroundImage ? 'no-repeat' : null"
      [style.--zoom-level]="zoomLevel()"
      (dragover)="onDragOver($event)"
      (drop)="onDrop($event)"
    >
      <div class="guide-left"></div>
      <div class="guide-right"></div>
      <div class="guide-top"></div>
      <div class="guide-bottom"></div>

      <canvas #confettiCanvas class="confetti-canvas"></canvas>

      @for (notificationWidget of notificationWidgets$ | async; track notificationWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(notificationWidget)"
          [draggable]="draggable"
          [disableBackground]="true"
          [forceOnTop]="true"
          (mousedown)="onWidgetMouseDown(notificationWidget, WidgetType.NOTIFICATION, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-notification
            [notificationWidget]="getWidget(notificationWidget)"
            [draggable]="draggable"
            [testData]="testData"
          ></gb-presenter-widget-notification>
        </gb-presenter-widget-wrapper>
      }

      @for (textWidget of textWidgets$ | async; track textWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(textWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(textWidget, WidgetType.TEXT, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-text
            [textWidget]="getWidget(textWidget)"
            [editable]="draggable"
            (textCommitted)="onTextWidgetCommitted(textWidget.uuid, $event)"
            (editFinished)="onTextWidgetEditFinished(textWidget.uuid)"
          ></gb-presenter-widget-text>
        </gb-presenter-widget-wrapper>
      }

      @for (smartTextWidget of smartTextWidgets$ | async; track smartTextWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(smartTextWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(smartTextWidget, WidgetType.SMART_TEXT, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-smart-text
            [auction]="auction$ | async"
            [testData]="testData"
            [smartTextWidget]="getWidget(smartTextWidget)"
          ></gb-presenter-widget-smart-text>
        </gb-presenter-widget-wrapper>
      }

      @for (itemTableWidget of itemTableWidgets$ | async; track itemTableWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(itemTableWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(itemTableWidget, WidgetType.ITEM_TABLE, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-item-table
            [testData]="testData"
            [isTemplateMode]="isTemplateMode"
            [itemTableWidget]="getWidget(itemTableWidget)"
          ></gb-presenter-widget-item-table>
        </gb-presenter-widget-wrapper>
      }

      @for (imageWidget of imageWidgets$ | async; track imageWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(imageWidget)"
          [draggable]="draggable"
          [lockAspectRatio]="true"
          [cornerOnlyResize]="true"
          (mousedown)="onWidgetMouseDown(imageWidget, WidgetType.IMAGE, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-image [imageWidget]="getWidget(imageWidget)"></gb-presenter-widget-image>
        </gb-presenter-widget-wrapper>
      }

      @for (totalRaisedWidget of totalRaisedWidgets$ | async; track totalRaisedWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(totalRaisedWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(totalRaisedWidget, WidgetType.TOTAL_RAISED, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-total-raised
            [testData]="testData"
            [totalRaisedWidget]="getWidget(totalRaisedWidget)"
          ></gb-presenter-widget-total-raised>
        </gb-presenter-widget-wrapper>
      }

      @for (countdownWidget of countdownWidgets$ | async; track countdownWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(countdownWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(countdownWidget, WidgetType.COUNTDOWN, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-countdown [countdownWidget]="getWidget(countdownWidget)"></gb-presenter-widget-countdown>
        </gb-presenter-widget-wrapper>
      }

      @for (honourRollWidget of honourRollWidgets$ | async; track honourRollWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(honourRollWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(honourRollWidget, WidgetType.HONOUR_ROLL, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-honour-roll
            [honourRollWidget]="getWidget(honourRollWidget)"
            [testData]="testData"
            [inPreviewMode]="inPreviewMode"
            [canvasWidth]="designDimensions?.width || 0"
            [canvasHeight]="designDimensions?.height || 0"
            [isTemplateMode]="isTemplateMode"
          ></gb-presenter-widget-honour-roll>
        </gb-presenter-widget-wrapper>
      }

      @for (raffleDrawWidget of raffleDrawWidgets$ | async; track raffleDrawWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(raffleDrawWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(raffleDrawWidget, WidgetType.RAFFLE_DRAW, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-raffle-draw
            (openRaffleItemWarning)="openRaffleItemWarning.emit(raffleDrawWidget)"
            [inPreviewMode]="inPreviewMode"
            [raffleDrawWidget]="getWidget(raffleDrawWidget)"
            (confettiRequested)="fireConfetti()"
            [isTemplateMode]="isTemplateMode"
            [testData]="testData"
          ></gb-presenter-widget-raffle-draw>
        </gb-presenter-widget-wrapper>
      }

      @for (raffleWinnersWidget of raffleWinnersWidgets$ | async; track raffleWinnersWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(raffleWinnersWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(raffleWinnersWidget, WidgetType.RAFFLE_WINNERS, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-raffle-winners-list
            (openRaffleItemWarning)="openRaffleItemWarning.emit(raffleWinnersWidget)"
            [inPreviewMode]="inPreviewMode"
            [isTemplateMode]="isTemplateMode"
            [testData]="testData"
            [raffleWinnersWidget]="getWidget(raffleWinnersWidget)"
          ></gb-presenter-widget-raffle-winners-list>
        </gb-presenter-widget-wrapper>
      }

      @for (thermometerWidget of thermometerWidgets$ | async; track thermometerWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(thermometerWidget)"
          [draggable]="draggable"
          [lockAspectRatio]="true"
          [cornerOnlyResize]="true"
          (mousedown)="onWidgetMouseDown(thermometerWidget, WidgetType.THERMOMETER, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-thermometer
            [testData]="testData"
            [thermometerWidget]="getWidget(thermometerWidget)"
          ></gb-presenter-widget-thermometer>
        </gb-presenter-widget-wrapper>
      }

      @for (qrCodeWidget of qrCodeWidgets$ | async; track qrCodeWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(qrCodeWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(qrCodeWidget, WidgetType.QR_CODE, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-qr-code [qrCodeWidget]="getWidget(qrCodeWidget)"></gb-presenter-widget-qr-code>
        </gb-presenter-widget-wrapper>
      }

      @for (teamScoringWidget of teamScoringWidgets$ | async; track teamScoringWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(teamScoringWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(teamScoringWidget, WidgetType.TEAM_SCORING, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-team-scoring
            [testData]="testData"
            [teamScoringWidget]="getWidget(teamScoringWidget)"
          ></gb-presenter-widget-team-scoring>
        </gb-presenter-widget-wrapper>
      }

      @for (powerPledgeWidget of powerPledgeWidgets$ | async; track powerPledgeWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(powerPledgeWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(powerPledgeWidget, WidgetType.POWER_PLEDGE, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-power-pledge
            [testData]="testData"
            [powerPledgeWidget]="getWidget(powerPledgeWidget)"
          ></gb-presenter-widget-power-pledge>
        </gb-presenter-widget-wrapper>
      }

      @for (videoWidget of videoWidgets$ | async; track videoWidget.uuid) {
        <gb-presenter-widget-wrapper
          [widget]="getWidget(videoWidget)"
          [draggable]="draggable"
          (mousedown)="onWidgetMouseDown(videoWidget, WidgetType.VIDEO, $event)"
          (positionChanged)="onWidgetPositionChanged($event)"
          [zoomLevel]="zoomLevel"
        >
          <gb-presenter-widget-video
            [draggable]="!inPreviewMode"
            [videoWidget]="getWidget(videoWidget)"
          ></gb-presenter-widget-video>
        </gb-presenter-widget-wrapper>
      }
    </div>
  </ng-template>

  @if (!inPreviewMode) {
    <div
      class="projector-canvas-padding"
      [style.width.px]="((designDimensions?.width || 0) + 20) * zoomLevel()"
      [style.height.px]="((designDimensions?.height || 0) + 20) * zoomLevel()"
    >
      <ng-container *ngTemplateOutlet="canvasContent"></ng-container>
    </div>
  } @else {
    <ng-container *ngTemplateOutlet="canvasContent"></ng-container>
  }
</div>
